import { type JSX, useEffect, useState } from "react";
import clsx from "clsx";
import type { IStepData, IRadioRatingItemData, ITableData } from "@/service/assessment";
import { BackButton, TextArea } from "@/shared/ui";
import { AssessmentTable, type TableSelection } from "../assessment-table";
import { StepCard } from "../step-card";
import { RadioGroup } from "../radio-group";
import { ShowLegendButton } from "../show-legend-button";
import { FeedbackCard } from "../feedback-card";

interface StepConstructorProps extends IStepData {
  onSubmitData: (stepId: string, data: any, previousData?: any) => void;
  onClickBack: () => void;
  isLoadingSubmit?: boolean;
  isLoadingBack?: boolean;
  assessmentId?: number;
}

export const StepConstructor = (props: StepConstructorProps): JSX.Element => {
  const {
    type,
    skills,
    dimension,
    subdimension,
    data,
    withDescription,
    isRequired,
    onSubmitData,
    onClickBack,
    stepId,
    isLoadingSubmit,
    isLoadingBack,
    assessedFirstName,
    assessedLastName,
    assessedRole,
    stepCurrent,
    stepsTotal,
    assessmentId,
    submittedValue,
    submittedValues,
  } = props;

  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [textAreaValue, setTextAreaValue] = useState<string>("");
  const [showLegend, setShowLegend] = useState<boolean>(false);

  const cardSubtitle =
    type === "rating" && skills?.length ? (
      <>
        <b>Skills:</b> {skills.join(", ")}
      </>
    ) : (
      ""
    );

  const initial: TableSelection[] =
    type === "table" ? (data as ITableData).rows.map((r) => ({ id: r.id, value: null })) : [];
  const [selections, setSelections] = useState<TableSelection[]>(initial);

  useEffect(() => {
    setSelectedValue(submittedValue ?? null);
    setTextAreaValue(submittedValue ?? "");
    setSelections(
      submittedValues
        ? (data as ITableData).rows.map((row) => ({
            id: row.id,
            value: submittedValues?.[row.id]?.toString() ?? null,
          }))
        : initial,
    );
    setShowLegend(false);
  }, [stepId]);

  const stepTypeControl = () => {
    if (type === "radio" || type === "rating") {
      const options = (data as IRadioRatingItemData[]).map((item) => ({
        label: item.name,
        value: item.id,
        description: item.description,
      }));

      return (
        <div className="flex w-full justify-between gap-8 p-10">
          <RadioGroup
            className={type === "radio" ? "w-1/2" : "w-full"}
            showDescription={showLegend}
            direction={type === "radio" ? "column" : "row"}
            showRadio={type === "radio"}
            options={options}
            value={selectedValue}
            onChange={setSelectedValue}
          />
          {type === "radio" && (
            <ul className="font-family-third flex w-1/2 list-none flex-col gap-3 text-sm">
              {options.map((item) => (
                <li key={item.description} className="block">
                  <b>{item.label}: </b>
                  {item.description}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    if (type === "textarea" && subdimension === "Additional Feedback") {
      return (
        <FeedbackCard
          assessmentId={assessmentId}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTextAreaValue(e.target.value)}
        />
      );
    }

    if (type === "textarea") {
      return (
        <TextArea
          value={textAreaValue}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTextAreaValue(e.target.value)}
          className="!h-[260px] max-h-[360px] max-w-full"
        />
      );
    }

    if (type === "table") {
      return <AssessmentTable data={data as ITableData} selections={selections} onChange={setSelections} />;
    }

    return null;
  };

  const onSubmit = () => {
    if (type === "table") {
      const previousData = submittedValues
        ? (data as ITableData).rows.map((r) => ({
            id: r.id,
            value: submittedValues?.[r.id]?.toString() ?? null,
          }))
        : undefined;
      onSubmitData(stepId, selections, previousData);

      return;
    }

    if (type === "radio" || type === "rating") {
      const previousData = submittedValue ? [{ id: submittedValue }] : undefined;
      onSubmitData(stepId, [{ id: selectedValue }], previousData);

      return;
    }

    if (type === "textarea") {
      const previousData = submittedValue ?? undefined;
      onSubmitData(stepId, textAreaValue, previousData);
    }
  };

  const isSubmitDisabled =
    isRequired &&
    ((type === "table" && selections.some((i) => i.value === null)) ||
      ((type === "radio" || type === "rating") && !selectedValue) ||
      (type === "textarea" && textAreaValue.trim().length === 0));

  return (
    <div className="flex flex-col gap-6">
      <BackButton />
      <StepCard
        headerControl={
          withDescription &&
          type === "rating" && <ShowLegendButton showLegend={showLegend} setShowLegend={setShowLegend} />
        }
        subtitle={cardSubtitle}
        disabledSubmitBtn={isSubmitDisabled}
        isLoadingSubmit={isLoadingSubmit}
        isLoadingBack={isLoadingBack}
        title={`${dimension}${subdimension ? `: ${subdimension}` : ""}`}
        onSubmit={onSubmit}
        onClickBack={onClickBack}
        assessedFirstName={assessedFirstName}
        assessedLastName={assessedLastName}
        assessedRole={assessedRole}
        stepCurrent={stepCurrent}
        stepsTotal={stepsTotal}
      >
        <div className={clsx("w-full", (isLoadingSubmit || isLoadingBack) && "pointer-none opacity-70")}>
          {stepTypeControl()}
        </div>
      </StepCard>
    </div>
  );
};
